import {AtomEffect} from "recoil";
import {Dispatch, SetStateAction, useEffect, useState} from "react";

export interface PersistStorage {
    setItem(key: string, value: string): void | Promise<void>
    mergeItem?(key: string, value: string): Promise<void>
    getItem(key: string): null | string | Promise<null | string>
}

export interface PersistConverter {
    stringify: (value: any) => string
    parse: (value: string) => any
}

export interface PersistConfiguration {
    key?: string
    storage?: PersistStorage
    converter?: PersistConverter
}

/**
 * recoil 값을 sessionStorage에 저장해서 유지함.
 * @param config - Configuration options for the persist effect.
 * @returns An object with the persistAtom function as a property.
 */
export const recoilPersist = (config: PersistConfiguration = {}): {persistAtom: AtomEffect<any>} => {
    if (typeof window === 'undefined') {
        return {
            persistAtom: () => {},
        }
    }

    const { key = 'recoil-persist', storage = sessionStorage, converter = JSON } = config

    const persistAtom: AtomEffect<any> = ({ onSet, node, trigger, setSelf }) => {
        if (trigger === 'get') {
            const state = getState()
            if (typeof state.then === 'function') {
                state.then((s) => {
                    if (s.hasOwnProperty(node.key)) {
                        setSelf(s[node.key])
                    }
                })
            }
            if (state.hasOwnProperty(node.key)) {
                setSelf(state[node.key])
            }
        }

        onSet(async (newValue, _, isReset) => {
            const state = getState()
            if (typeof state.then === 'function') {
                state.then((s: any) => updateState(newValue, s, node.key, isReset))
            } else {
                updateState(newValue, state, node.key, isReset)
            }
        })
    }

    const updateState = (
        newValue: any,
        state: any,
        key: string,
        isReset: boolean,
    ) => {
        if (isReset) {
            delete state[key]
        } else {
            state[key] = newValue
        }

        setState(state)
    }

    const getState = (): any => {
        const toParse = storage.getItem(key)
        if (toParse === null || toParse === undefined) {
            return {}
        }
        if (typeof toParse === 'string') {
            return parseState(toParse)
        }

        if (typeof toParse.then === 'function') {
            return toParse.then(parseState)
        }

        return {}
    }

    const parseState = (state?: string | null | undefined) => {
        if (state === undefined || state === null) {
            return {}
        }
        try {
            return converter.parse(state)
        } catch (e) {
            console.error(e)
            return {}
        }
    }

    const setState = (state: any): void => {
        try {
            if (typeof storage.mergeItem === 'function') {
                storage.mergeItem(key, converter.stringify(state))
            } else {
                storage.setItem(key, converter.stringify(state))
            }
        } catch (e) {
            console.error(e)
        }
    }

    return { persistAtom }
}

/**
 * context 값을 sessionStorage에 저장해서 값을 유지함.
 */
export const readItemGroupFromSessionStorage = (key: string): any => {
    const itemGroupString = sessionStorage.getItem(key);
    if (itemGroupString) {
        return JSON.parse(itemGroupString);
    }
    return {};
}

export const useContextState = <T>(
    key: string,
    defaultValue?: T
): [T, Dispatch<SetStateAction<T>>] => {
    const [state, setState] = useState<T | any>(() => {
       const itemGroup =  readItemGroupFromSessionStorage(key);
       if(itemGroup) return itemGroup;
       if(defaultValue) return defaultValue;
    });

    useEffect(() => {
        sessionStorage.setItem(key, JSON.stringify(state));
    }, [state])

    return [state, setState];
}
